import { getLocationBranchByLocationID, getDistrictByAmphurID } from '../../utils/requests';
import { LocationItem } from '../../types/search';
import { highlightText } from './utils';
import { loadProvinceSubLocations, isProvinceLocationHandling } from './provinceLocationHandler';

let debounceTimer: NodeJS.Timeout | null = null;

let lastFullBranches: { [key: string]: LocationItem[] } = {};

interface NoResultItem extends LocationItem {
  isNoResult?: boolean;
}

export const loadSubLocations = async (
  location: LocationItem,
  searchQuery: string,
  setLoadingLocationData: (loading: boolean) => void,
  setSelectedMainOption: (option: LocationItem) => void,
  locale: string = 'th'
) => {
  if (!location) {
    return;
  }

  if (isProvinceLocationHandling(location)) {
    await loadProvinceSubLocations(location, searchQuery, setLoadingLocationData, setSelectedMainOption, locale);
    return;
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(async () => {
    try {
      const locationCacheKey = `loc_${location.value}`;
      const hasCache = lastFullBranches[locationCacheKey]?.length > 0;
      
      if (!hasCache) {
        setLoadingLocationData(true);
      }

      let branches: LocationItem[] = [];
      
      if (location.children && location.children.length > 0 && !lastFullBranches[locationCacheKey]) {
        lastFullBranches[locationCacheKey] = [...location.children];
      }
      
      if (lastFullBranches[locationCacheKey]?.length > 0) {
        branches = lastFullBranches[locationCacheKey];
      } else {
        const response = await getLocationBranchByLocationID(`${location.value}`);
        
        if (!response || !response.data) {
          if (location.children && location.children.length > 0) {
            branches = location.children;
          } else {
            setSelectedMainOption({
              ...location,
              children: []
            });
            setLoadingLocationData(false);
            return;
          }
        } else {
          if (Array.isArray(response.data)) {
            branches = formatBranches(response.data, location, searchQuery);
          } else if (response.data.children && Array.isArray(response.data.children)) {
            branches = formatBranches(response.data.children, location, searchQuery);
          } else if (response.data.data && Array.isArray(response.data.data)) {
            branches = formatBranches(response.data.data, location, searchQuery);
          } else if (response.data.items && Array.isArray(response.data.items)) {
            branches = formatBranches(response.data.items, location, searchQuery);
          } else if (response.data.branches && Array.isArray(response.data.branches)) {
            branches = formatBranches(response.data.branches, location, searchQuery);
          }
          
          if (branches.length > 0) {
            lastFullBranches[locationCacheKey] = [...branches];
          }
        }
      }
      
      let filteredBranches: LocationItem[] = [...branches];
      if (searchQuery && searchQuery.trim() !== '') {
        filteredBranches = branches.filter(branch => 
          branch.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        if (filteredBranches.length === 0) {
          filteredBranches = [...branches];
          
          const noResultItem: NoResultItem = {
            label: `ไม่พบ "${searchQuery}" ในตัวเลือกย่อย`,
            value: `no_result_${Date.now()}`,
            typeId: location.typeId,
            isNoResult: true
          };
          
          filteredBranches.unshift(noResultItem);
        } else {
          filteredBranches = filteredBranches.map(branch => {
            const highlighted = highlightText(branch.label, searchQuery);
            return {
              ...branch,
              displayLabel: highlighted.displayLabel,
              originalLabel: highlighted.originalLabel,
              rendered: highlighted.rendered
            };
          });
        }
      }
      
      const updatedLocation = {
        ...location,
        children: filteredBranches
      };
      
      setSelectedMainOption(updatedLocation);
    } catch (error) {
      if (location.children && location.children.length > 0) {
        setSelectedMainOption({
          ...location 
        });
      } else {
        setSelectedMainOption({
          ...location,
          children: []
        });
      }
    } finally {
      setLoadingLocationData(false);
    }
  }, 100);
};

const formatBranches = (data: any[], parentLocation: LocationItem, searchQuery: string): LocationItem[] => {
  const filteredData = data.filter((item: any) => {
    const name = item.station_name || // สำหรับสถานีรถไฟฟ้า
              item.Name_TH || 
              item.PROVINCE_NAME_TH || 
              item.location_type_name_TH || 
              item.branch_name_TH || 
              item.name || 
              item.label || 
              item.branch_name || 
              item.value || "ไม่มีชื่อ";
    return name !== "ไม่มีชื่อ" && name !== "ทั้งหมด" && name !== "";
  });
  
  return filteredData.map((item: any) => {
    const nameToCheck = (item.station_name || item.branch_name_TH || item.name || item.label || '').toLowerCase();
    const isHuaiKhwang = nameToCheck.includes('ห้วยขวาง');
    const isDonMueang = nameToCheck.includes('ดอนเมือง');
    
    let finalTypeId = parentLocation.typeId;
    
    if (parentLocation.typeId === 0 || isHuaiKhwang || (isDonMueang && !nameToCheck.includes('สนามบิน'))) {
      finalTypeId = 0;
    }
    
    const isTransitStation = nameToCheck.includes('สถานี') || parentLocation.typeId === 10;
    
    if (isTransitStation) {
      return {
        label: item.station_name || item.branch_name_TH || item.name || "ไม่มีชื่อ",
        value: item.station_id || item.branch_id || item.id || item.value || 0,
        lat: item.latitude || item.lat || item.branch_lat || 0,
        lng: item.longitude || item.lng || item.branch_lng || 0,
        typeId: 10, // กำหนด typeId เป็น 10 สำหรับรถไฟฟ้า
        originalLabel: item.station_name || item.branch_name_TH || item.name || "ไม่มีชื่อ",
        rendered: false,
        parentId: parentLocation.value.toString(),
        parentName: parentLocation.label
      };
    }
    
    return {
      label: item.station_name || 
             item.Name_TH || 
             item.PROVINCE_NAME_TH || 
             item.location_type_name_TH || 
             item.branch_name_TH || 
             item.name || 
             item.branch_name || 
             "ไม่มีชื่อ",
      value: item.station_id || 
             item.ID || 
             item.PROVINCE_ID || 
             item.location_type_ID || 
             item.branch_id || 
             item.value || 
             0,
      lat: item.latitude || 
           item.LAT || 
           item.PROVINCE_LAT || 
           item.branch_lat || 
           0,
      lng: item.longitude || 
           item.LONG || 
           item.PROVINCE_LON || 
           item.branch_lng || 
           0,
      typeId: finalTypeId,
      originalLabel: item.station_name || 
                     item.Name_TH || 
                     item.PROVINCE_NAME_TH || 
                     item.location_type_name_TH || 
                     item.branch_name_TH || 
                     item.name || 
                     item.branch_name || 
                     "ไม่มีชื่อ",
      rendered: false,
      parentId: parentLocation.value.toString(),
      parentName: parentLocation.label
    };
  });
}; 